import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { SERVICES } from "@/lib/api";
import api from "@/lib/api";
import { SERVICE_FIELDS } from "@/lib/schemas";
import { useAuth } from "@/lib/auth";
import DynamicField from "@/components/DynamicField";
import SlotPicker from "@/components/SlotPicker";
import { toast } from "sonner";
import { ArrowRight, Clock, MapPin, Wallet } from "@phosphor-icons/react";

const SLOT_SERVICES = ["spa", "barber", "washingbay"];

// Best-effort guess at which field holds the booking date for slot lookup
const DATE_FIELD_BY_SERVICE = {
  spa: "preferred_date",
  barber: "appointment_date",
  washingbay: "appointment_time", // datetime-local
};

export default function BookService() {
  const { serviceKey } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const service = SERVICES.find((s) => s.key === serviceKey);
  const schema = SERVICE_FIELDS[serviceKey];
  const [data, setData] = useState({});
  const [slotTime, setSlotTime] = useState("");
  const [applyWallet, setApplyWallet] = useState(false);
  const [wallet, setWallet] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setData((d) => ({ ...d, full_name: user.name || "", email: user.email || "", phone: user.phone || "" }));
      api.get("/wallet").then((r) => setWallet(r.data.balance || 0)).catch(() => {});
    }
  }, [user]);

  const total = useMemo(() => schema?.priceField?.(data) || 0, [data, schema]);
  const walletUsed = applyWallet ? Math.min(wallet, total) : 0;
  const finalTotal = Math.max(0, total - walletUsed);

  if (!service || !schema) {
    return <div className="p-20 text-center">Service not found. <Link to="/services" className="underline">Browse services</Link></div>;
  }

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const usesSlots = SLOT_SERVICES.includes(serviceKey);
  const dateField = DATE_FIELD_BY_SERVICE[serviceKey];
  // Extract a YYYY-MM-DD from whatever field holds the date
  const rawDate = data[dateField] || "";
  const slotDate = rawDate?.slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Please sign in to book.");
      nav("/login", { state: { from: `/book/${serviceKey}` } });
      return;
    }
    if (usesSlots && !slotTime) {
      toast.error("Please select an available time slot.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        service: serviceKey,
        details: data,
        amount: total,
        payment_method: "mobile_money",
        slot_date: usesSlots ? slotDate : null,
        slot_time: usesSlots ? slotTime : null,
        apply_wallet: applyWallet,
      });
      toast.success("Booking created");
      if ((booking.amount || 0) === 0) {
        nav("/dashboard");
      } else {
        nav(`/payment/${booking.id}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Booking failed";
      toast.error(typeof msg === "string" ? msg : "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid={`book-${serviceKey}-page`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Sticky details */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="relative aspect-[4/5] overflow-hidden border border-black">
              <img src={service.image} alt={service.name} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <p className="font-mono text-xs tracking-widest text-white/70">{serviceKey.toUpperCase()}</p>
                <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-none mt-2">{service.name}</h1>
                <p className="mt-2 text-white/70 text-sm">{service.tagline}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="border border-black p-4 flex items-center gap-3">
                <Clock size={18} />
                <div>
                  <p className="label-eyebrow text-black/50">Open</p>
                  <p className="text-sm font-semibold">24 / 7</p>
                </div>
              </div>
              <div className="border border-black p-4 flex items-center gap-3">
                <MapPin size={18} />
                <div>
                  <p className="label-eyebrow text-black/50">Location</p>
                  <p className="text-sm font-semibold">Accra, Ghana</p>
                </div>
              </div>
            </div>
            <div className="border border-black p-4 mt-3">
              <p className="label-eyebrow text-black/50">Estimated total</p>
              <p className="font-display text-4xl font-black mt-1" data-testid="booking-estimate">GHS {finalTotal.toFixed(2)}</p>
              {walletUsed > 0 && (
                <p className="text-xs text-black/60 mt-1 font-mono">
                  GHS {total.toFixed(2)} − {walletUsed.toFixed(2)} wallet
                </p>
              )}
              <p className="text-xs text-black/50 mt-2">Pay via Mobile Money at checkout.</p>
            </div>

            <Link to={`/services/${serviceKey}`} className="btn-outline w-full mt-3 text-center" data-testid="link-service-detail">
              See reviews & details
            </Link>
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-7">
          <p className="label-eyebrow">Booking form</p>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-none mt-3 mb-10">{schema.title}</h2>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {schema.fields.map((f) => (
              <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                <DynamicField field={f} value={data[f.name]} onChange={setField} context={`booking-${serviceKey}`} />
              </div>
            ))}

            {usesSlots && (
              <div className="md:col-span-2 border-t border-black/10 pt-6 mt-2">
                <SlotPicker service={serviceKey} date={slotDate} value={slotTime} onChange={setSlotTime} />
              </div>
            )}

            {user && wallet > 0 && (
              <div className="md:col-span-2 border-t border-black/10 pt-6 mt-2">
                <label className="flex items-center gap-3 cursor-pointer" data-testid="apply-wallet-toggle">
                  <input
                    type="checkbox"
                    checked={applyWallet}
                    onChange={(e) => setApplyWallet(e.target.checked)}
                    className="w-5 h-5 accent-black"
                  />
                  <span className="flex items-center gap-2 text-sm">
                    <Wallet size={18} />
                    Apply wallet balance —{" "}
                    <b className="font-mono">GHS {wallet.toFixed(2)}</b>
                  </span>
                </label>
              </div>
            )}

            <div className="md:col-span-2 flex flex-wrap items-center gap-4 pt-4">
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="booking-submit">
                {submitting ? "Submitting…" : finalTotal === 0 ? "Confirm booking" : "Continue to payment"} <ArrowRight size={16} />
              </button>
              <Link to="/services" className="btn-ghost">Cancel</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
